/**
 * Visual Schema Builder Component
 *
 * Interactive node-based schema editor using ReactFlow.
 * Features:
 * - Drag and drop nodes for classes and enums
 * - Right-click context menus
 * - Color coding based on schema origin (blue=base, green=extended, purple=custom)
 * - Add/edit/delete attributes, classes, and enums
 * - Bidirectional sync with YAML
 */

import React, { useState, useCallback, useMemo } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  BackgroundVariant,
  NodeTypes,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, Lock, ChevronRight, ChevronDown, Trash2, X } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type SchemaNodeType = 'builtin' | 'extended' | 'custom';

export interface SchemaAttribute {
  name: string;
  type: string;
  description?: string;
  required?: boolean;
  multivalued?: boolean;
  sync_to_square?: boolean;
  default_value?: string;
  pattern?: string;
  minimum?: number;
  maximum?: number;
  source?: SchemaNodeType; // Which schema this attribute comes from
}

export interface SchemaClass {
  name: string;
  description?: string;
  attributes: SchemaAttribute[];
  source: SchemaNodeType;
  extends?: string; // Parent class if inheriting
  abstract?: boolean;
}

export interface SchemaEnum {
  name: string;
  description?: string;
  values: string[];
  source: SchemaNodeType;
}

export interface SchemaCategory {
  name: string;
  description?: string;
  classes: string[]; // Class names in this category
}

export interface VisualSchemaData {
  classes: SchemaClass[];
  enums: SchemaEnum[];
  categories: SchemaCategory[];
}

// ============================================================================
// Node Data Types
// ============================================================================

interface ClassNodeData {
  label: string;
  schemaClass: SchemaClass;
  onAddAttribute?: (className: string) => void;
  onEdit?: (className: string) => void;
  onDelete?: (className: string) => void;
}

interface EnumNodeData {
  label: string;
  schemaEnum: SchemaEnum;
  onEdit?: (enumName: string) => void;
  onDelete?: (enumName: string) => void;
}

// ============================================================================
// Custom Node Components
// ============================================================================

/**
 * Class Node Component (UML Style)
 * Shows class name and optionally attributes with color coding
 */
const ClassNode: React.FC<{ data: ClassNodeData }> = ({ data }) => {
  const { schemaClass, onAddAttribute, onEdit, onDelete } = data;
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const borderColor =
    schemaClass.source === 'builtin'
      ? 'border-blue-500'
      : schemaClass.source === 'extended'
      ? 'border-green-500'
      : 'border-purple-500';

  const bgColor =
    schemaClass.source === 'builtin'
      ? 'bg-blue-50'
      : schemaClass.source === 'extended'
      ? 'bg-green-50'
      : 'bg-purple-50';

  const isReadOnly = schemaClass.source === 'builtin';

  return (
    <div
      className={`bg-white rounded shadow-md border-2 ${borderColor} ${bgColor} min-w-[180px] max-w-[280px]`}
      onContextMenu={(e) => {
        e.preventDefault();
        setShowContextMenu(!showContextMenu);
      }}
    >
      {/* UML Class Header */}
      <div
        className={`p-2 border-b ${borderColor} flex items-center justify-between cursor-pointer hover:bg-opacity-80`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2 flex-1">
          {isReadOnly && <Lock className="h-3 w-3 text-blue-600" />}
          <div className="flex-1">
            <div className="text-xs text-gray-500 italic">«class»</div>
            <h3 className="font-bold text-sm text-gray-900">{schemaClass.name}</h3>
          </div>
        </div>
        <div className="text-gray-400">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>
      </div>

      {/* UML Inheritance (is_a) */}
      {schemaClass.extends && (
        <div className={`px-2 py-1 border-b ${borderColor} bg-gray-50 text-xs flex items-center justify-center gap-1`}>
          <span className="text-gray-600 italic">is_a:</span>
          <span className="font-semibold text-gray-800">{schemaClass.extends}</span>
        </div>
      )}

      {/* Attributes Section (Collapsible) */}
      {isExpanded && (
        <>
          <div className="p-2 space-y-1">
            {schemaClass.attributes.length > 0 ? (
              schemaClass.attributes.map((attr) => {
                const attrColor =
                  attr.source === 'builtin'
                    ? 'text-blue-600'
                    : attr.source === 'extended'
                    ? 'text-green-600'
                    : 'text-purple-600';

                return (
                  <div key={attr.name} className="flex items-center gap-1 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full ${attrColor.replace('text-', 'bg-')}`} />
                    <span className="font-mono text-gray-700">{attr.name}</span>
                    <span className="text-gray-500">: {attr.type}</span>
                    {attr.required && <span className="text-red-500">*</span>}
                    {attr.sync_to_square && (
                      <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1 rounded">
                        SQ
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-xs text-gray-400 italic">No attributes</div>
            )}

            {/* Add Attribute Button */}
            {onAddAttribute && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAddAttribute(schemaClass.name);
                }}
                className="w-full mt-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded flex items-center justify-center gap-1 border border-dashed border-gray-300"
              >
                <Plus className="h-3 w-3" />
                Add Attribute
              </button>
            )}
          </div>
        </>
      )}

      {/* Collapsed State - Show Attribute Count */}
      {!isExpanded && schemaClass.attributes.length > 0 && (
        <div className="p-2 text-xs text-gray-500 text-center">
          {schemaClass.attributes.length} attribute{schemaClass.attributes.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Context Menu */}
      {showContextMenu && (
        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-50 min-w-[150px]">
          {onAddAttribute && (
            <button
              onClick={() => {
                onAddAttribute(schemaClass.name);
                setShowContextMenu(false);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Attribute
            </button>
          )}
          <button
            onClick={() => {
              setIsExpanded(!isExpanded);
              setShowContextMenu(false);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
          {!isReadOnly && onDelete && (
            <button
              onClick={() => {
                onDelete(schemaClass.name);
                setShowContextMenu(false);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete Class
            </button>
          )}
          {isReadOnly && (
            <div className="px-4 py-2 text-sm text-gray-500 italic">
              Base schema (read-only)
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {schemaClass.description && (
        <div className={`p-2 border-t ${borderColor} text-[10px] text-gray-600 bg-gray-50`}>
          {schemaClass.description}
        </div>
      )}
    </div>
  );
};

/**
 * Enum Node Component (UML Style)
 * Shows enum name and optionally values with color coding
 */
const EnumNode: React.FC<{ data: EnumNodeData }> = ({ data }) => {
  const { schemaEnum, onDelete } = data;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);

  // Use orange/amber colors for enums to differentiate from classes
  const borderColor =
    schemaEnum.source === 'builtin'
      ? 'border-orange-500'
      : schemaEnum.source === 'extended'
      ? 'border-amber-500'
      : 'border-yellow-600';

  const bgColor =
    schemaEnum.source === 'builtin'
      ? 'bg-orange-50'
      : schemaEnum.source === 'extended'
      ? 'bg-amber-50'
      : 'bg-yellow-50';

  const isReadOnly = schemaEnum.source === 'builtin';

  return (
    <div
      className={`bg-white rounded shadow-md border-2 ${borderColor} ${bgColor} min-w-[160px] max-w-[240px]`}
      onContextMenu={(e) => {
        e.preventDefault();
        setShowContextMenu(!showContextMenu);
      }}
    >
      {/* UML Enum Header */}
      <div
        className={`p-2 border-b ${borderColor} flex items-center justify-between cursor-pointer hover:bg-opacity-80`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2 flex-1">
          {isReadOnly && <Lock className="h-3 w-3 text-orange-600" />}
          <div className="flex-1">
            <div className="text-xs text-gray-500 italic">«enumeration»</div>
            <h3 className="font-bold text-sm text-gray-900">{schemaEnum.name}</h3>
          </div>
        </div>
        <div className="text-gray-400">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </div>
      </div>

      {/* Values Section (Collapsible) */}
      {isExpanded && (
        <div className="p-2 space-y-1">
          {schemaEnum.values.length > 0 ? (
            schemaEnum.values.map((value) => (
              <div key={value} className="flex items-center gap-1 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                <span className="font-mono text-gray-700">{value}</span>
              </div>
            ))
          ) : (
            <div className="text-xs text-gray-400 italic">No values</div>
          )}
        </div>
      )}

      {/* Collapsed State - Show Value Count */}
      {!isExpanded && schemaEnum.values.length > 0 && (
        <div className="p-2 text-xs text-gray-500 text-center">
          {schemaEnum.values.length} value{schemaEnum.values.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Context Menu */}
      {showContextMenu && (
        <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-md border border-gray-200 z-50 min-w-[150px]">
          <button
            onClick={() => {
              setIsExpanded(!isExpanded);
              setShowContextMenu(false);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
          {!isReadOnly && onDelete && (
            <button
              onClick={() => {
                onDelete(schemaEnum.name);
                setShowContextMenu(false);
              }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              Delete Enum
            </button>
          )}
          {isReadOnly && (
            <div className="px-4 py-2 text-sm text-gray-500 italic">
              Base schema (read-only)
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {schemaEnum.description && (
        <div className={`p-2 border-t ${borderColor} text-[10px] text-gray-600 bg-gray-50`}>
          {schemaEnum.description}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface VisualSchemaBuilderProps {
  schema: VisualSchemaData;
  schemaType: SchemaNodeType;
  onChange?: (schema: VisualSchemaData) => void;
  onYamlChange?: (yaml: string) => void;
}

export const VisualSchemaBuilder: React.FC<VisualSchemaBuilderProps> = ({
  schema,
  schemaType,
  onChange,
  onYamlChange,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Dialog state
  const [addAttributeDialog, setAddAttributeDialog] = useState<{ open: boolean; className: string }>({ open: false, className: '' });
  const [addClassDialog, setAddClassDialog] = useState(false);
  const [addEnumDialog, setAddEnumDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: 'class' | 'enum'; name: string }>({ open: false, type: 'class', name: '' });

  // Form state
  const [attributeForm, setAttributeForm] = useState<Partial<SchemaAttribute>>({});
  const [classForm, setClassForm] = useState<Partial<SchemaClass>>({ source: schemaType });
  const [enumForm, setEnumForm] = useState<Partial<SchemaEnum> & { valuesText?: string }>({ source: schemaType, values: [], valuesText: '' });

  // Define custom node types
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      class: ClassNode,
      enum: EnumNode,
    }),
    []
  );

  // Convert schema to nodes
  React.useEffect(() => {
    const newNodes: Node[] = [];
    let yOffset = 0;
    const xSpacing = 350;
    const ySpacing = 200;

    // Create class nodes
    schema.classes.forEach((schemaClass, index) => {
      const col = index % 3;
      const row = Math.floor(index / 3);

      // Read-only logic:
      // - When editing a builtin template directly (schemaType === 'builtin'): EDITABLE
      // - When viewing inherited builtin in extended schema (schemaType === 'extended' && source === 'builtin'): READ-ONLY
      // - Custom schemas: Always editable
      const isReadOnly = schemaType === 'extended' && schemaClass.source === 'builtin';

      newNodes.push({
        id: `class-${schemaClass.name}`,
        type: 'class',
        position: { x: col * xSpacing, y: row * ySpacing },
        data: {
          label: schemaClass.name,
          schemaClass,
          onAddAttribute: !isReadOnly ? handleAddAttribute : undefined,
          onDelete: !isReadOnly ? handleDeleteClass : undefined,
        },
      });
    });

    // Create enum nodes
    yOffset = Math.ceil(schema.classes.length / 3) * ySpacing;
    schema.enums.forEach((schemaEnum, index) => {
      const col = index % 3;
      const row = Math.floor(index / 3);

      const isReadOnly = schemaType === 'extended' && schemaEnum.source === 'builtin';

      newNodes.push({
        id: `enum-${schemaEnum.name}`,
        type: 'enum',
        position: { x: col * xSpacing, y: yOffset + row * ySpacing },
        data: {
          label: schemaEnum.name,
          schemaEnum,
          onDelete: !isReadOnly ? handleDeleteEnum : undefined,
        },
      });
    });

    setNodes(newNodes);

    // Create UML-style inheritance edges
    const newEdges: Edge[] = [];
    schema.classes.forEach((schemaClass) => {
      if (schemaClass.extends) {
        newEdges.push({
          id: `${schemaClass.name}-extends-${schemaClass.extends}`,
          source: `class-${schemaClass.extends}`,
          target: `class-${schemaClass.name}`,
          type: 'smoothstep',
          animated: false,
          markerEnd: {
            type: 'arrowclosed',
            width: 20,
            height: 20,
            color: '#3b82f6',
          },
          style: {
            stroke: '#3b82f6',
            strokeWidth: 2,
            strokeDasharray: '5,5'
          },
          label: '⇐ inherits',
          labelStyle: { fill: '#6b7280', fontSize: 10 },
        });
      }
    });

    setEdges(newEdges);
  }, [schema, schemaType]);

  // Handlers
  const handleAddAttribute = useCallback((className: string) => {
    setAddAttributeDialog({ open: true, className });
    setAttributeForm({ source: schemaType, required: false });
  }, [schemaType]);

  const handleDeleteClass = useCallback((className: string) => {
    setDeleteDialog({ open: true, type: 'class', name: className });
  }, []);

  const handleDeleteEnum = useCallback((enumName: string) => {
    setDeleteDialog({ open: true, type: 'enum', name: enumName });
  }, []);

  const confirmAddAttribute = useCallback(() => {
    if (!attributeForm.name || !addAttributeDialog.className) return;

    const newAttribute: SchemaAttribute = {
      name: attributeForm.name,
      type: attributeForm.type || 'string',
      required: attributeForm.required || false,
      source: attributeForm.source || schemaType,
      description: attributeForm.description,
      multivalued: attributeForm.multivalued,
      sync_to_square: attributeForm.sync_to_square,
    };

    const updatedSchema = {
      ...schema,
      classes: schema.classes.map(cls =>
        cls.name === addAttributeDialog.className
          ? { ...cls, attributes: [...cls.attributes, newAttribute] }
          : cls
      ),
    };

    onChange?.(updatedSchema);
    setAddAttributeDialog({ open: false, className: '' });
    setAttributeForm({});
  }, [attributeForm, addAttributeDialog, schema, schemaType, onChange]);

  const confirmAddClass = useCallback(() => {
    if (!classForm.name) return;

    const newClass: SchemaClass = {
      name: classForm.name,
      description: classForm.description,
      attributes: [],
      source: classForm.source || schemaType,
      extends: classForm.extends,
      abstract: classForm.abstract,
    };

    const updatedSchema = {
      ...schema,
      classes: [...schema.classes, newClass],
    };

    onChange?.(updatedSchema);
    setAddClassDialog(false);
    setClassForm({ source: schemaType });
  }, [classForm, schema, schemaType, onChange]);

  const confirmAddEnum = useCallback(() => {
    if (!enumForm.name || !enumForm.valuesText?.trim()) return;

    // Parse values from text (one per line)
    const values = enumForm.valuesText
      .split('\n')
      .map(v => v.trim())
      .filter(v => v.length > 0);

    if (values.length === 0) return;

    const newEnum: SchemaEnum = {
      name: enumForm.name,
      description: enumForm.description,
      values,
      source: enumForm.source || schemaType,
    };

    const updatedSchema = {
      ...schema,
      enums: [...schema.enums, newEnum],
    };

    onChange?.(updatedSchema);
    setAddEnumDialog(false);
    setEnumForm({ source: schemaType, values: [], valuesText: '' });
  }, [enumForm, schema, schemaType, onChange]);

  const confirmDelete = useCallback(() => {
    if (deleteDialog.type === 'class') {
      const updatedSchema = {
        ...schema,
        classes: schema.classes.filter(cls => cls.name !== deleteDialog.name),
      };
      onChange?.(updatedSchema);
    } else {
      const updatedSchema = {
        ...schema,
        enums: schema.enums.filter(enm => enm.name !== deleteDialog.name),
      };
      onChange?.(updatedSchema);
    }

    setDeleteDialog({ open: false, type: 'class', name: '' });
  }, [deleteDialog, schema, onChange]);

  const onConnect = useCallback(
    (connection: Connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges]
  );

  return (
    <div className="h-full w-full border rounded-lg bg-gray-50 relative">
      {/* Floating Action Buttons */}
      <div className="absolute top-4 left-4 z-10 flex gap-2">
        <button
          onClick={() => setAddClassDialog(true)}
          className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Class
        </button>
        <button
          onClick={() => setAddEnumDialog(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Enum
        </button>
      </div>

      {/* Legend */}
      <div className="absolute top-4 right-4 z-10 bg-white shadow-md rounded-lg p-3 space-y-2">
        <h4 className="text-sm font-semibold text-gray-900">Legend</h4>
        <div className="space-y-2">
          {/* Classes */}
          <div>
            <div className="text-xs font-semibold text-gray-700 mb-1">Classes</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-500 bg-blue-50 rounded"></div>
                <span>Builtin (read-only)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-green-500 bg-green-50 rounded"></div>
                <span>Extended (editable)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-purple-500 bg-purple-50 rounded"></div>
                <span>Custom (editable)</span>
              </div>
            </div>
          </div>
          {/* Enums */}
          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-gray-700 mb-1">Enumerations</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-orange-500 bg-orange-50 rounded"></div>
                <span>Builtin (read-only)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-amber-500 bg-amber-50 rounded"></div>
                <span>Extended (editable)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-yellow-600 bg-yellow-50 rounded"></div>
                <span>Custom (editable)</span>
              </div>
            </div>
          </div>
          {/* UML Symbols */}
          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-gray-700 mb-1">UML</div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="text-blue-500">→</div>
                <span>Inheritance (dashed)</span>
              </div>
              <div className="flex items-center gap-2">
                <ChevronRight className="h-3 w-3 text-gray-400" />
                <span>Click to expand</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            if (node.type === 'class') {
              const data = node.data as ClassNodeData;
              return data.schemaClass.source === 'builtin'
                ? '#3b82f6'  // blue
                : data.schemaClass.source === 'extended'
                ? '#10b981'  // green
                : '#a855f7'; // purple
            } else if (node.type === 'enum') {
              const data = node.data as EnumNodeData;
              return data.schemaEnum.source === 'builtin'
                ? '#f97316'  // orange
                : data.schemaEnum.source === 'extended'
                ? '#f59e0b'  // amber
                : '#ca8a04'; // yellow-600
            }
            return '#9ca3af'; // gray
          }}
        />
      </ReactFlow>

      {/* Add Attribute Dialog */}
      {addAttributeDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="border-b px-6 py-4 bg-purple-50">
              <h2 className="text-xl font-semibold text-purple-900">Add Attribute to {addAttributeDialog.className}</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={attributeForm.name || ''}
                  onChange={(e) => setAttributeForm({ ...attributeForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500"
                  placeholder="attribute_name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={attributeForm.type || 'string'}
                  onChange={(e) => setAttributeForm({ ...attributeForm, type: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500"
                >
                  <option value="string">String</option>
                  <option value="integer">Integer</option>
                  <option value="float">Float</option>
                  <option value="boolean">Boolean</option>
                  <option value="date">Date</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={attributeForm.description || ''}
                  onChange={(e) => setAttributeForm({ ...attributeForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500"
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={attributeForm.required || false}
                    onChange={(e) => setAttributeForm({ ...attributeForm, required: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Required</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={attributeForm.multivalued || false}
                    onChange={(e) => setAttributeForm({ ...attributeForm, multivalued: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Multivalued</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={attributeForm.sync_to_square || false}
                    onChange={(e) => setAttributeForm({ ...attributeForm, sync_to_square: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-700">Sync to Square</span>
                </label>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
              <button
                onClick={() => {
                  setAddAttributeDialog({ open: false, className: '' });
                  setAttributeForm({});
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddAttribute}
                disabled={!attributeForm.name}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Attribute
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Class Dialog */}
      {addClassDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="border-b px-6 py-4 bg-purple-50">
              <h2 className="text-xl font-semibold text-purple-900">Add New Class</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={classForm.name || ''}
                  onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500"
                  placeholder="ClassName"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={classForm.description || ''}
                  onChange={(e) => setClassForm({ ...classForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Extends (Parent Class)</label>
                <select
                  value={classForm.extends || ''}
                  onChange={(e) => setClassForm({ ...classForm, extends: e.target.value || undefined })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">None</option>
                  {schema.classes.map(cls => (
                    <option key={cls.name} value={cls.name}>{cls.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
              <button
                onClick={() => {
                  setAddClassDialog(false);
                  setClassForm({ source: schemaType });
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddClass}
                disabled={!classForm.name}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Class
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Enum Dialog */}
      {addEnumDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="border-b px-6 py-4 bg-orange-50">
              <h2 className="text-xl font-semibold text-orange-900">Add New Enum</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={enumForm.name || ''}
                  onChange={(e) => setEnumForm({ ...enumForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-orange-500"
                  placeholder="EnumName"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={enumForm.description || ''}
                  onChange={(e) => setEnumForm({ ...enumForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-orange-500"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Values (one per line) *</label>
                <textarea
                  value={enumForm.valuesText || ''}
                  onChange={(e) => setEnumForm({ ...enumForm, valuesText: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-orange-500 font-mono text-sm"
                  rows={6}
                  placeholder="value1&#10;value2&#10;value3"
                />
              </div>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
              <button
                onClick={() => {
                  setAddEnumDialog(false);
                  setEnumForm({ source: schemaType, values: [], valuesText: '' });
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddEnum}
                disabled={!enumForm.name || !enumForm.valuesText?.trim()}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Enum
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="border-b px-6 py-4 bg-red-50">
              <h2 className="text-xl font-semibold text-red-900 flex items-center gap-2">
                <Trash2 className="h-6 w-6" />
                Delete {deleteDialog.type === 'class' ? 'Class' : 'Enum'}
              </h2>
            </div>
            <div className="p-6">
              <p className="text-gray-900">
                Are you sure you want to delete <span className="font-mono font-semibold text-red-600">{deleteDialog.name}</span>?
              </p>
              <p className="text-sm text-gray-600 mt-2">This action cannot be undone.</p>
            </div>
            <div className="border-t px-6 py-4 flex justify-end gap-2 bg-gray-50">
              <button
                onClick={() => setDeleteDialog({ open: false, type: 'class', name: '' })}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
